import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Camera, Loader2, Eye, EyeOff, Mail, Phone, Lock, User, Globe } from 'lucide-react';
import { AUTH } from '@/constants/testIds';
import COUNTRIES from '@/data/countries';
import VideosAnnonces from '@/components/VideosAnnonces';

const AuthPage = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  // Login state
  const [loginIdentifier, setLoginIdentifier] = useState(''); // Peut être email ou téléphone
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Register state
  const [regFullName, setRegFullName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [regCountry, setRegCountry] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);

  // Handle Google OAuth callback
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const hash = location.hash;
      if (!hash) return;

      const params = new URLSearchParams(hash.slice(1));
      const session_id = params.get('session_id');

      if (session_id) {
        try {
          setLoading(true);
          const response = await api.post('/auth/google/callback', { session_id });

          if (response.data.success) {
            toast.success(`Bienvenue ${response.data.user.name}!`);
            // Reload to trigger cookie-based auth
            window.location.href = '/dashboard';
          }
        } catch (error) {
          console.error('Google auth error:', error);
          toast.error('Authentification échouée. Veuillez réessayer.');
        } finally {
          setLoading(false);
        }
      }
    };

    handleGoogleCallback();
  }, [location.hash]);

  // Google Sign-In handler
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await api.get('/admin/countries/enabled');
        const enabled = res.data.enabled_countries || [];
        const filtered = enabled.length === 0 ? COUNTRIES : COUNTRIES.filter(c => enabled.includes(c.code));
        setAvailableCountries(filtered);
      } catch (error) {
        setAvailableCountries(COUNTRIES);
      }
    };
    fetchCountries();
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La photo ne doit pas dépasser 5MB');
        return;
      }
      
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginIdentifier || !loginPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      await login(loginIdentifier, loginPassword);
      
      // Redirection immédiate sans attendre
      toast.success('✅ Connexion réussie !', { duration: 2000 });
      
      // Utiliser setTimeout(0) pour ne pas bloquer l'UI
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
      
    } catch (error) {
      setLoading(false); // Seulement si erreur
      toast.error(error.response?.data?.detail || 'Email/Téléphone ou mot de passe incorrect');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!regFullName || !regPhone || !regEmail || !regPassword || !regConfirmPassword || !regCountry) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!profilePhoto) {
      toast.error('Veuillez ajouter une photo de profil');
      return;
    }

    // Valider l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(regEmail)) {
      toast.error('Adresse email invalide');
      return;
    }

    // Valider le téléphone
    if (!regPhone.startsWith('+')) {
      toast.error('Le numéro de téléphone doit commencer par + et l\'indicatif pays (ex: +243...)');
      return;
    }

    // Vérifier la correspondance des mots de passe
    if (regPassword !== regConfirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    // Vérifier la force du mot de passe
    if (regPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('full_name', regFullName);
      formData.append('phone_number', regPhone);
      formData.append('email', regEmail);
      formData.append('password', regPassword);
      formData.append('country', regCountry);
      formData.append('profile_picture', profilePhoto);

      await register(formData);
      
      // Redirection immédiate après inscription
      toast.success('✅ Inscription réussie ! Bienvenue sur AGRP BOX', { duration: 2000 });
      
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
      
    } catch (error) {
      setLoading(false); // Seulement si erreur
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-zinc-900 to-black p-4">
      <div className="w-full max-w-4xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="AGRP BOX Logo" 
            className="h-16 w-auto mx-auto mb-4 object-contain"
          />
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
            <span className="text-[#E50914]">AGRP BOX</span>
          </h1>
          <p className="text-zinc-400">Plateforme de distribution musicale</p>
        </div>

        <Card className="glass border-zinc-800">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-zinc-900">
              <TabsTrigger value="login" className="data-[state=active]:bg-[#E50914]">
                Connexion
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-[#E50914]">
                Inscription
              </TabsTrigger>
            </TabsList>

            {/* ONGLET CONNEXION */}
            <TabsContent value="login">
              <CardHeader>
                <CardTitle>Connexion</CardTitle>
                <CardDescription>
                  Connectez-vous avec votre email ou téléphone
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="loginIdentifier" className="text-white">
                      Email ou Numéro de téléphone
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-zinc-400" />
                      <Input
                        id="loginIdentifier"
                        type="text"
                        value={loginIdentifier}
                        onChange={(e) => setLoginIdentifier(e.target.value)}
                        placeholder="admin@agrpbox.com ou +243..."
                        className="bg-zinc-900 border-zinc-800 text-white pl-10"
                        data-testid={AUTH.loginPhone}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="loginPassword" className="text-white">
                      Mot de passe
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-zinc-400" />
                      <Input
                        id="loginPassword"
                        type={showLoginPassword ? 'text' : 'password'}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="Votre mot de passe"
                        className="bg-zinc-900 border-zinc-800 text-white pl-10 pr-10"
                        data-testid={AUTH.loginPassword}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-3 text-zinc-400 hover:text-white"
                      >
                        {showLoginPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#E50914] hover:bg-[#F8212C] text-white py-6 text-lg font-semibold"
                    data-testid={AUTH.loginSubmit}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Connexion...
                      </>
                    ) : (
                      'Se connecter'
                    )}
                  </Button>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-zinc-700"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-zinc-900 text-zinc-400">OU</span>
                    </div>
                  </div>

                  {/* Google Sign-in Button */}
                  <Button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-3 border border-gray-300 rounded-lg shadow transition duration-150 flex items-center justify-center gap-3"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Se connecter avec Google
                  </Button>
                </form>
              </CardContent>

              {/* Annonces / Publicités en bas de la page Connexion */}
              <VideosAnnonces />
            </TabsContent>

            {/* ONGLET INSCRIPTION */}
            <TabsContent value="register">
              <CardHeader>
                <CardTitle>Créer un compte</CardTitle>
                <CardDescription>
                  Remplissez tous les champs pour vous inscrire
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* Photo de profil en cercle */}
                  <div className="flex flex-col items-center mb-6">
                    <div className="relative">
                      <div className="w-32 h-32 rounded-full bg-zinc-800 border-4 border-[#E50914] flex items-center justify-center overflow-hidden">
                        {photoPreview ? (
                          <img 
                            src={photoPreview} 
                            alt="Aperçu" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Camera size={40} className="text-zinc-500" />
                        )}
                      </div>
                      <label 
                        htmlFor="profilePhoto" 
                        className="absolute bottom-0 right-0 bg-[#E50914] hover:bg-[#F8212C] rounded-full p-2 cursor-pointer transition-all"
                      >
                        <Camera size={20} className="text-white" />
                      </label>
                      <input
                        id="profilePhoto"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </div>
                    <p className="text-zinc-400 text-xs mt-2">Photo de profil (max 5MB)</p>
                  </div>

                  {/* Nom complet */}
                  <div>
                    <Label htmlFor="regFullName" className="text-white">
                      Nom complet *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-5 w-5 text-zinc-400" />
                      <Input
                        id="regFullName"
                        type="text"
                        value={regFullName}
                        onChange={(e) => setRegFullName(e.target.value)}
                        placeholder="Votre nom complet"
                        className="bg-zinc-900 border-zinc-800 text-white pl-10"
                        required
                      />
                    </div>
                  </div>

                  {/* Numéro de téléphone */}
                  <div>
                    <Label htmlFor="regPhone" className="text-white">
                      Numéro de téléphone (avec indicatif pays) *
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-5 w-5 text-zinc-400" />
                      <Input
                        id="regPhone"
                        type="tel"
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value)}
                        placeholder="+243..."
                        className="bg-zinc-900 border-zinc-800 text-white pl-10"
                        required
                      />
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      Format: +indicatif + numéro (ex: +243999888777)
                    </p>
                  </div>

                  {/* Adresse e-mail */}
                  <div>
                    <Label htmlFor="regEmail" className="text-white">
                      Adresse e-mail *
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-zinc-400" />
                      <Input
                        id="regEmail"
                        type="email"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        placeholder="votre.email@exemple.com"
                        className="bg-zinc-900 border-zinc-800 text-white pl-10"
                        required
                      />
                    </div>
                  </div>

                  {/* Mot de passe */}
                  <div>
                    <Label htmlFor="regPassword" className="text-white">
                      Mot de passe *
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-zinc-400" />
                      <Input
                        id="regPassword"
                        type={showRegPassword ? 'text' : 'password'}
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        placeholder="Minimum 8 caractères"
                        className="bg-zinc-900 border-zinc-800 text-white pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPassword(!showRegPassword)}
                        className="absolute right-3 top-3 text-zinc-400 hover:text-white"
                      >
                        {showRegPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Confirmer mot de passe */}
                  <div>
                    <Label htmlFor="regConfirmPassword" className="text-white">
                      Confirmer mot de passe *
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-zinc-400" />
                      <Input
                        id="regConfirmPassword"
                        type={showRegConfirmPassword ? 'text' : 'password'}
                        value={regConfirmPassword}
                        onChange={(e) => setRegConfirmPassword(e.target.value)}
                        placeholder="Répétez le mot de passe"
                        className="bg-zinc-900 border-zinc-800 text-white pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)}
                        className="absolute right-3 top-3 text-zinc-400 hover:text-white"
                      >
                        {showRegConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Choisir son pays */}
                  <div>
                    <Label htmlFor="regCountry" className="text-white">
                      Choisir votre pays *
                    </Label>
                    <Select value={regCountry} onValueChange={setRegCountry} required>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                        <SelectValue placeholder="Sélectionnez votre pays" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-white max-h-60">
                        {availableCountries.map((country) => (
                          <SelectItem key={country.code} value={country.name}>
                            <div className="flex items-center gap-2">
                              <span>{country.flag}</span>
                              <span>{country.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#E50914] hover:bg-[#F8212C] text-white py-6 text-lg font-semibold"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Inscription en cours...
                      </>
                    ) : (
                      'S\'INSCRIRE'
                    )}
                  </Button>
                </form>
              </CardContent>

              {/* Annonces / Publicités en bas de la page Inscription */}
              <VideosAnnonces />
            </TabsContent>
          </Tabs>
        </Card>

        {/* Lien de retour */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-zinc-400 hover:text-white transition-colors text-sm"
          >
            ← Retour à l'accueil
          </button>
        </div>
      </div> {/* Fin div max-w-4xl */}
    </div>
  );
};

export default AuthPage;
